/**
 * mwApp.localizations module <br>
 * Work with localizations: show/add/edit/remove
 *
 * @namespace mwApp/localizations
 */

/**
 * ConfigRoutes
 * Configurates module routes
 *
 * @class ConfigRoutes
 * @memberof mwApp/localizations
 * @see mwApp/localizations
 *
 * @ngdoc config
 * @name mwApp/localizations.config:ConfigRoutes
 */
/* @ngInject */
export default function ConfigRoutes ($stateProvider) {
  $stateProvider

    /**
     * abstract state for /app-localization
     */
    .state('app.localizations', {
      abstract: true,
      url: '/app-localization',
      template: '<ui-view/>',
      resolve: {
        scope: /* @ngInject */ function ($state, authSvc, InappSettings) {
          if (!authSvc.hasEnabledFlag('FEATURE_APP_LOCALIZATION_ENABLED')) {
            return $state.go('app.404')
          }
          // Block access if multiorg is enabled - localization should be managed per organization
          return InappSettings.lazyLoad()
            .then(inapp => {
              if (inapp && inapp.multiorgEnabled === true) {
                return $state.go('app.404')
              }
              return {
                featureEnabled: true
              }
            })
            .catch(() => {
              // If InappSettings fails to load, allow access (fallback)
              return {
                featureEnabled: true
              }
            })
        }
      }
    })

    /**
     * controller {@link mwApp/localizations.controller:LocalizationsCtrl}
     * @name /app-localization
     * @memberof mwApp/localizations.config:ConfigRoutes
     */
    .state('app.localizations.list', {
      url: '',
      templateUrl: 'components/localization/views/localizations.html',
      controller: 'LocalizationsCtrl as vm',
      label: 'App Localization',
      access: ''
    })

    /**
     * intermediate state for /app-localization/application (for breadcrumbs)
     */
    .state('app.localizations.application', {
      url: '/applications',
      label: 'Shopping Cart',
      access: '',
      redirectTo: function (trans) {
        const $state = trans.router.stateService
        $state.restoreHash = 'applications'
        return { state: 'app.localizations.list' }
      }
    })

    /**
     * state for /app-localization/application/:localizationId
     */
    .state('app.localizations.application.detail', {
      url: '/:localizationId',
      templateUrl: 'components/localization/views/addLocalization.html',
      controller: 'AddLocalizationCtrl as vm',
      label: '{{::localizationId | addOrEdit}} Localization',
      access: '',
      resolve: {
        enabled: /* @ngInject */ function ($state, authSvc) {
          if (!authSvc.hasEnabledFlag('FEATURE_SHOPPING_CART_MANAGEMENT_ENABLED')) {
            $state.go('app.403')
          }
        },
        localization: /* @ngInject */ function ($transition$, Localization) {
          const { localizationId: locale } = $transition$.params()
          return locale !== 'new'
            ? Localization.loadLocale({ localization: { locale: { tag: locale } }, appName: 'scp' })
            : new Localization({ appName: 'scp', localization: { dictionary: {}, locale: null } })
        },
        localizations: /* @ngInject */ function (Localization) {
          return Localization.loadAll({ appName: 'scp' })
            .then(data => { return data })
        },
        glossary: /* @ngInject */ function (Localization) {
          return Localization.getGlossary('scp')
        }
      }
    })

    /**
     * intermediate state for /app-localization/client (for breadcrumbs)
     */
    .state('app.localizations.client', {
      url: '/clients',
      label: 'Client App',
      access: '',
      redirectTo: function (trans) {
        const $state = trans.router.stateService
        $state.restoreHash = 'clients'
        return { state: 'app.localizations.list' }
      }
    })

    /**
     * state for /app-localization/client/:localizationId
     */
    .state('app.localizations.client.detail', {
      url: '/:localizationId',
      templateUrl: 'components/localization/views/addLocalization.html',
      controller: 'AddLocalizationCtrl as vm',
      label: '{{::localizationId | addOrEdit}} Localization',
      access: '',
      resolve: {
        localization: /* @ngInject */ function ($transition$, Localization) {
          const { localizationId: locale } = $transition$.params()
          return locale !== 'new'
            ? Localization.loadLocale({ localization: { locale: { tag: locale } }, appName: 'client-apps' })
            : new Localization({ appName: 'client-apps', localization: { dictionary: {}, locale: null } })
        },
        localizations: /* @ngInject */ function (Localization) {
          return Localization.loadAll({ appName: 'client-apps' })
            .then(data => { return data })
        },
        glossary: /* @ngInject */ function (Localization) {
          return Localization.getGlossary('client-apps')
        }
      }
    })
}
